<template>
  <form
    class="conversation--form"
    @submit.prevent="onFormSubmit"
    @keydown="handleFormKeydown"
  >
    <div>
      <div class="row">
        <div class="columns">
          <label :class="{ error: $v.targetInbox.$error }">
            {{ $t('NEW_CONVERSATION.FORM.INBOX.LABEL') }}
            <select v-model="targetInbox">
              <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox">
                {{ inbox.name }}
              </option>
            </select>
            <span v-if="$v.targetInbox.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.INBOX.ERROR') }}
            </span>
          </label>
        </div>
        <div
          v-if="(isDraft || !contact.phone_number) && !isByEmail"
          class="columns"
          style="margin-inline-start: 1rem; flex: 0.8"
        >
          <label :class="{ error: $v.contactInner.phone_number.$error }">
            {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}
            <contact-input-search
              :phone-number.sync="contactInner.phone_number"
              field="phone_number"
              @on-select-contact="onSelectContact"
              @input="setPhoneNumber"
            />
            <span v-if="$v.contactInner.phone_number.$error" class="message">
              {{ !$v.contactInner.phone_number.isValidPhoneStart ? $t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR2') : $t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR') }}
            </span>
          </label>
        </div>
        <div
          v-if="isByEmail"
          class="columns"
          style="margin-inline-start: 1rem; flex: 0.8"
        >
          <label :class="{ error: $v.contactInner.email.$error }">
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
            <contact-input-search
              :email.sync="contactInner.email"
              field="email"
              @on-select-contact="onSelectContactEmail"
              @input="setEmail"
            />
            <span v-if="$v.contactInner.email.$error" class="message">
              {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
            </span>
          </label>
        </div>
        <div class="columns" style="margin-inline-start: 1rem; flex: 1.2">
          <label>
            {{
              contact.phone_number
                ? $t('NEW_CONVERSATION.FORM.TO.LABEL')
                : $t('CONTACT_FORM.FORM.NAME.LABEL')
            }}
            <input
              v-model.trim="contactInner.name"
              type="text"
              :placeholder="$t('CONTACT_FORM.FORM.NAME.PLACEHOLDER')"
            />
          </label>
        </div>
      </div>
      <div class="row mb-2">
        <label
          v-tooltip.top="$t('NEW_CONVERSATION.FORM.ASSIGNEE_ME_TOOLTIP')"
          class="flex items-center gap-2 w-fit"
        >
          <input
            v-model="isAssigneeMe"
            type="checkbox"
            class="semantic-checkbox mx-0"
          />
          <span>{{ $t('NEW_CONVERSATION.FORM.ASSIGNEE_ME_LABEL') }}</span>
        </label>
      </div>
      <div class="row">
        <div class="columns">
          <div class="canned-response">
            <canned-response
              v-if="showCannedResponseMenu && hasSlashCommand"
              :search-key="cannedResponseSearchKey"
              @click="replaceTextWithCannedResponse"
            />
          </div>
          <online-wa-templates
            v-if="isOnlineWaInboxV2"
            :inbox-id="targetInbox.id"
            @on-select-template="toggleWaTemplate"
            @on-send="onSendWhatsAppReply"
          />
          <whatsapp-templates
            v-else-if="hasWhatsappTemplates"
            :inbox-id="targetInbox.id"
            @on-select-template="toggleWaTemplate"
            @on-send="onSendWhatsAppReply"
          />
          <div v-else-if="isAnEmailChannel">
            <div class="w-full">
              <label :class="{ error: $v.subject.$error }">
                {{ $t('NEW_CONVERSATION.FORM.SUBJECT.LABEL') }}
                <input
                  v-model="subject"
                  type="text"
                  :placeholder="$t('NEW_CONVERSATION.FORM.SUBJECT.PLACEHOLDER')"
                  @input="$v.subject.$touch"
                />
                <span v-if="$v.subject.$error" class="message">
                  {{ $t('NEW_CONVERSATION.FORM.SUBJECT.ERROR') }}
                </span>
              </label>
            </div>
            <div class="w-full">
              <label>
                {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
              </label>
              <div
                class="border border-neutral-200 rounded-lg overflow-hidden mb-3"
              >
                <reply-email-head
                  :cc-emails.sync="ccEmails"
                  :bcc-emails.sync="bccEmails"
                />
                <div class="editor-wrap !mb-0">
                  <email-woot-message-editor
                    ref="messageEditor"
                    v-model="message"
                    class="message-editor"
                    :inbox="targetInbox"
                    :class="{ editor_warning: $v.message.$error }"
                    :enable-variables="true"
                    :signature="signatureToApply"
                    :allow-signature="true"
                    :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
                    :update-selection-with="updateEditorSelectionWith"
                    :show-emoji-picker="showEmojiPicker"
                    :toggle-emoji-picker="toggleEmojiPicker"
                    :hide-emoji-picker="hideEmojiPicker"
                    :attached-files="attachedFiles"
                    :remove-attachment="removeAttachment"
                    @clear-selection="clearEditorSelection"
                    @toggle-canned-menu="toggleCannedMenu"
                    @blur="$v.message.$touch"
                  >
                    <template #toolbar-left>
                      <div
                        v-if="isEmailOrWebWidgetInbox"
                        class="flex h-8 hover:bg-gray-100 rounded-md cursor-pointer"
                      >
                        <file-upload
                          ref="uploadAttachment"
                          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                          input-id="newConversationAttachment"
                          :size="4096 * 4096"
                          :accept="allowedFileTypes"
                          :multiple="true"
                          :drop="true"
                          :drop-directory="false"
                          :data="{
                            direct_upload_url:
                              '/rails/active_storage/direct_uploads',
                            direct_upload: true,
                          }"
                          @input-file="
                            file =>
                              fileUploadHandler(file, {
                                isMailboxFileUpload: isAnEmailChannel,
                                isWhatsAppFileUpload: isAWhatsAppChannel,
                              })
                          "
                        >
                          <span
                            :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                            class="material-symbols-outlined message-toolbar-item"
                            @click="triggerOpenUploadFile"
                          >
                            attach_file
                          </span>
                        </file-upload>
                      </div>
                      <div v-if="showDocalButton" class="flex">
                        <span
                          v-tooltip.top="$t('INTEGRATION_SETTINGS.DOCAL.CTA_MODAL.TITLE')"
                          :class="{ disabled: !isDocalContactValid }"
                          class="material-symbols-outlined message-toolbar-item"
                          @click="isDocalContactValid && toggleDocal()"
                        >
                          contract_edit
                        </span>
                        <docal-documents-modal
                          :show="showDocalModal"
                          :contact-email="
                            isAnEmailChannel ? contactInner.email : ''
                          "
                          :contact-phone="
                            isASmsInbox ? contactInner.phone_number : ''
                          "
                          :inbox-id="targetInbox.id"
                          @signing-link="onDocalSigningLink"
                          @cancel="showDocalModal = false"
                        />
                      </div>
                      <div v-if="showEncryptFilesButton" class="flex">
                        <span
                          v-tooltip.top="encryptFilesTooltip"
                          class="material-symbols-outlined message-toolbar-item"
                          :class="{ 'active encrypt-files-on': encryptFiles }"
                          @click="toggleEncryptFiles"
                        >
                          {{ encryptFiles ? 'lock' : 'lock_open_right' }}
                        </span>
                      </div>
                      <div v-if="showFormsButton" class="flex">
                        <span
                          v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_FORMS')"
                          :class="{ disabled: !isFormsContactValid }"
                          class="material-symbols-outlined message-toolbar-item"
                          @click="isFormsContactValid && toggleForms()"
                        >
                          assignment
                        </span>
                      </div>
                    </template>
                    <template
                      v-if="isMessageLengthReachingThreshold"
                      #warning-message
                    >
                      <div class="text-xs px-4 py-1 text-right">
                        <span :class="charLengthClass">
                          {{ `${message.length}/${maxLength}` }}
                        </span>
                      </div>
                    </template>
                    <template
                      v-if="isMessageLengthReachingThreshold"
                      #warning-message
                    >
                      <div class="text-xs px-4 py-1 text-right">
                        <span :class="charLengthClass">
                          {{ `${message.length}/${maxLength}` }}
                        </span>
                      </div>
                    </template>
                  </email-woot-message-editor>
                  <span v-if="$v.message.$error" class="editor-warning__message">
                    {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <label v-else :class="{ error: $v.message.$error }">
            {{ $t('NEW_CONVERSATION.FORM.MESSAGE.LABEL') }}
            <textarea
              v-model="message"
              class="message-input"
              type="text"
              :placeholder="$t('NEW_CONVERSATION.FORM.MESSAGE.PLACEHOLDER')"
              @input="$v.message.$touch"
            />
            <span v-if="$v.message.$error" class="message">
              {{ $t('NEW_CONVERSATION.FORM.MESSAGE.ERROR') }}
            </span>
            <div v-if="showFormsButton" class="flex">
              <span
                v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_FORMS')"
                :class="{ disabled: !isFormsContactValid }"
                class="material-symbols-outlined message-toolbar-item"
                @click.prevent="isFormsContactValid && toggleForms()"
              >
                assignment
              </span>
            </div>
          </label>
        </div>
      </div>
    </div>
    <forms-list-modal
      v-if="showFormsButton"
      :show="showFormsModal"
      :inbox-id="targetInbox.id"
      :contact="formsContact"
      @form-link="onFormLink"
      @cancel="showFormsModal = false"
    />
    <div v-if="!hasWhatsappTemplates" class="modal-footer">
      <span
        v-if="showEncryptFilesButton && encryptFiles"
        class="encrypt-files-chip self-center ltr:mr-auto rtl:ml-auto"
      >
        <span class="material-symbols-outlined text-[16px]/[16px]">lock</span>
        {{ $t('CONVERSATION.REPLYBOX.ENCRYPT_FILES_ACTIVE') }}
      </span>
      <woot-button
        v-if="!isSingleWindow"
        class="cancel-filter-btn button clear text-base px-5 py-3 leading-[20px] h-[48px] font-[400]"
        color-scheme="secondary"
        @click.prevent="onCancel"
      >
        {{ $t('NEW_CONVERSATION.FORM.CANCEL') }}
      </woot-button>
      <woot-submit-button
        :disabled="!isAbleToSendMsg"
        :button-text="$t('NEW_CONVERSATION.FORM.SUBMIT')"
        :loading="conversationsUiFlags.isCreating"
        class="button-filled text-base px-5 py-3 font-[400] h-[48px]"
      />
    </div>
  </form>
</template>

<script>
import { mapGetters } from 'vuex';
import EmailWootMessageEditor from 'dashboard/components/widgets/WootWriter/EmailEditor/IndexV2.vue';
import ReplyEmailHead from 'dashboard/components/widgets/conversation/ReplyEmailHead';
import CannedResponse from 'dashboard/components/widgets/conversation/CannedResponse.vue';
import WhatsappTemplates from 'dashboard/routes/dashboard/conversation/contact/WhatsappTemplates.vue';
import ContactInputSearch from '../../modules/contact/components/ContactInputSearch.vue';
import alertMixin from 'shared/mixins/alertMixin';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';
import { required, requiredIf } from 'vuelidate/lib/validators';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import inboxMixin from 'shared/mixins/inboxMixin';
import OnlineWaTemplates from 'dashboard/components/widgets/conversation/watemplates/OnlineWaTemplates.vue';
import agentMixin from 'dashboard/mixins/agentMixin';
import {
  isPhoneIsrael,
  isValidPhoneStart,
} from '../../../shared/helpers/Validators';
import contactMixin from '../../mixins/contactMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import {
  appendSignature,
  removeSignature,
} from 'dashboard/helper/editorHelper';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { validEmailsByComma } from '../../components/widgets/conversation/helpers/emailHeadHelper';
import FileUpload from 'vue-upload-component';
import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import EmojiInput from 'shared/components/emoji/EmojiInput';
import rtlMixin from 'shared/mixins/rtlMixin';

const DocalDocumentsModal = () =>
  import('dashboard/components/widgets/conversation/DocalDocumentsModal.vue');
const FormsListModal = () =>
  import('dashboard/components/widgets/conversation/FormsListModal.vue');

export default {
  components: {
    ContactInputSearch,
    OnlineWaTemplates,
    EmailWootMessageEditor,
    ReplyEmailHead,
    CannedResponse,
    WhatsappTemplates,
    FileUpload,
    AttachmentPreview,
    DocalDocumentsModal,
    FormsListModal,
    EmojiInput,
  },
  mixins: [
    alertMixin,
    inboxMixin,
    agentMixin,
    contactMixin,
    uiSettingsMixin,
    fileUploadMixin,
    hasPermissionMixin,
    rtlMixin,
  ],
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    onSubmit: {
      type: Function,
      default: () => {},
    },
    singleInbox: {
      type: Object,
      default: undefined,
    },
    isSingleWindow: {
      type: Boolean,
      default: true,
    },
    isDraft: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      name: '',
      subject: '',
      message: '',
      showCannedResponseMenu: false,
      cannedResponseSearchKey: '',
      selectedInbox: '',
      isAssigningDefaultInbox: false,
      bccEmails: '',
      ccEmails: '',
      attachedFiles: [],
      messagePayload: undefined,
      isAssigneeMe: true,
      whatsappTemplateSelected: false,
      showDocalModal: false,
      encryptFiles: false,
      showFormsModal: false,
      showEmojiPicker: false,
      updateEditorSelectionWith: '',
      contactInner: {
        phone_number: this.contact?.phone_number,
        email: this.contact?.email,
        name:
          this.contact?.name || this.contact?.phone_number?.replace(/\+/, ''),
      },
    };
  },
  validations: {
    subject: {
      required: requiredIf('isAnEmailChannel'),
    },
    message: {
      requiredIfPresented: requiredIf(function () {
        return !this.isOnlineWaInboxV2;
      }),
    },
    targetInbox: {
      required,
    },
    contactInner: {
      phone_number: {
        isPhoneIsrael(val) {
          return isPhoneIsrael(val) || this.isAnEmailChannel;
        },
        isValidPhoneStart(val){
          return isValidPhoneStart(val);
        },
      },
      email: {
        required: requiredIf(function () {
          return this.isAnEmailChannel;
        }),
        hasValidEmails(value) {
          return validEmailsByComma(value || '');
        },
      },
    },
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      conversationsUiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      getMessageSignature: 'getMessageSignature',
      getAccount: 'accounts/getAccount',
      accountId: 'getCurrentAccountId',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    showDocalButton() {
      if (!this.account?.additional_attributes?.document_signature_enabled) return false;
      return this.isAnEmailChannel || this.isASmsInbox;
    },
    isContactEmailValid() {
      const email = this.contactInner.email;
      return email && validEmailsByComma(email);
    },
    isDocalEmailValid() {
      return this.isContactEmailValid;
    },
    isContactPhoneValid() {
      const phone = this.contactInner.phone_number;
      return phone && isPhoneIsrael(phone);
    },
    isDocalPhoneValid() {
      return this.isContactPhoneValid;
    },
    isDocalContactValid() {
      if (this.isAnEmailChannel) return this.isDocalEmailValid;
      if (this.isASmsInbox) return this.isDocalPhoneValid;
      return false;
    },
    showFormsButton() {
      return !!this.account?.forms_enabled;
    },
    // A form link is generated against the contact behind the typed details, so the button stays
    // disabled until there is a valid one to resolve.
    isFormsContactValid() {
      if (this.isAnEmailChannel) return this.isContactEmailValid;
      return this.isContactPhoneValid;
    },
    // The inbox decides which detail identifies the contact, so only that one is sent — otherwise a
    // stale value in the other field could resolve a different contact.
    formsContact() {
      const { name, email, phone_number: phone } = this.contactInner;
      if (this.isAnEmailChannel) return { name, email };
      return { name, phone_number: this.israelPhone2e164(phone) };
    },
    messageSignature() {
      const signature =
        this.getMessageSignature(this.targetInbox.id)?.body || '';
      if (this.attachmentSignature) {
        return (
          signature +
          `\n ![This is signature](${this.attachmentSignature} "This is signature")`
        );
      }
      return signature;
    },
    attachmentSignature() {
      return this.getMessageSignature(this.targetInbox.id)?.attachment_url;
    },
    emailMessagePayload() {
      const payload = {
        inboxId: this.targetInbox.id,
        contact: this.normalisedContact(),
        message: { content: this.message },
        assigneeId: this.isAssigneeMe ? this.currentUser.id : undefined,
      };
      if (this.isAnEmailChannel) {
        payload.contact.phone_number = undefined;
        payload.mailSubject = this.subject;
        payload.newConversation = true;

        if (this.ccEmails) {
          payload.message.cc_emails = this.ccEmails;
        }

        if (this.bccEmails) {
          payload.message.bcc_emails = this.bccEmails;
        }

        if (this.attachedFiles && this.attachedFiles.length) {
          payload.files = [];
          this.setAttachmentPayload(payload);
        }

        if (this.showEncryptFilesButton && this.encryptFiles) {
          payload.message.encrypt_files = true;
        }
      } else {
        payload.contact.email = undefined;
        payload.files = [];
      }
      return payload;
    },
    inboxes() {
      return this.$store.getters['inboxes/getInboxes'].filter(
        inbox =>
          this.hasInboxPermission('Inbox new message', inbox.id) &&
          this.isValidInbox(inbox) &&
          (inbox.channel_type !== 'Channel::Email' ||
            !this.contact?.phone_number) &&
          (!this.isDraft ||
            this.contact?.phone_number ||
            inbox.channel_type === 'Channel::Email') // only show email inboxes if from email links
      ); // todo create some global helper
    },
    targetInbox: {
      get() {
        return this.selectedInbox || {};
      },
      set(value) {
        this.selectedInbox = value;
        if (!this.isAssigningDefaultInbox && value?.id) {
          this.updateUISettings({
            last_used_new_message_inbox_id: value.id,
          });
        }
      },
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    isAnEmailInbox() {
      return this.isAnEmailChannel;
    },
    isEmailOrWebWidgetInbox() {
      // for the future
      return this.isAnEmailInbox || false;
    },
    isSignatureEnabledForInbox() {
      return this.isAnEmailInbox && this.sendWithSignature;
    },
    showEncryptFilesButton() {
      return this.globalConfig.analystShowEncrypt && this.isAnEmailChannel;
    },
    encryptFilesTooltip() {
      return this.encryptFiles
        ? this.$t('CONVERSATION.REPLYBOX.TIP_ENCRYPT_FILES_ON')
        : this.$t('CONVERSATION.REPLYBOX.TIP_ENCRYPT_FILES_OFF');
    },
    inbox() {
      return this.targetInbox || {};
    },
    hasWhatsappTemplates() {
      return !!this.targetInbox?.message_templates || this.isOnlineWaInboxV2; // d99d
    },
    sendWithSignature() {
      return !!this.messageSignature;
    },
    signatureToApply() {
      return this.messageSignature;
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    isByEmail() {
      return (
        this.isAnEmailChannel ||
        (!this.contact?.phone_number && this.contact?.email)
      );
    },
    maxLength() {
      return MESSAGE_MAX_LENGTH.EMAIL;
    },
    isMessageLengthReachingThreshold() {
      return this.isAnEmailInbox && this.message.length > this.maxLength - 50;
    },
    charLengthClass() {
      return this.message.length > this.maxLength
        ? 'text-red-600'
        : 'text-slate-600';
    },
    isAbleToSendMsg() {
      if (this.isAnEmailInbox && this.message.length > this.maxLength)
        return false;
      return this.inboxes.length > 0; // this.targetInbox
    },
    editorStateId() {
      return `draft-${this.contact?.phone_number}-${this.contact?.email}`;
    },
    emojiDialogClassOnRTLView() {
      if (this.isRTLView) {
        return 'emoji-dialog--rtl';
      }
      return '';
    },
  },

  watch: {
    message(value) {
      this.hasSlashCommand = value[0] === '/' && !this.isEmailOrWebWidgetInbox;
      const hasNextWord = value.includes(' ');
      const isShortCodeActive = this.hasSlashCommand && !hasNextWord;
      if (isShortCodeActive) {
        this.cannedResponseSearchKey = value.substring(1);
        this.showCannedResponseMenu = true;
      } else {
        this.cannedResponseSearchKey = '';
        this.showCannedResponseMenu = false;
      }
    },
    inboxes() {
      this.setDefaultInbox();
    },
    targetInbox() {
      this.message = '';
      this.setSignature();
    },
  },
  created() {
    this.setDefaultInbox();
  },
  mounted() {
    this.setFocusToFirstInput();
    this.setSignature();
  },
  methods: {
    triggerOpenUploadFile() {
      this.$refs.uploadAttachment.$el
        .querySelector('input[type="file"]')
        .click();
    },
    isValidInbox(inbox) {
      return (
        (inbox.api_type && inbox.api_type.charAt(0) === 'w') ||
        (inbox.channel_type === 'Channel::Sms' &&
          inbox.provider === '99digital') ||
        (inbox.channel_type === 'Channel::Email' && inbox.api_type?.length)
      );
    },
    handleFormKeydown(event) {
      if (event.key === 'Enter') {
        if (document.activeElement.type !== 'submit') {
          event.preventDefault();
        }
      }
    },
    setDefaultInbox() {
      if (this.inboxes.length && !this.targetInbox.id) {
        this.isAssigningDefaultInbox = true;
        if (
          this.singleInbox?.id &&
          this.inboxes.some(x => x.id === this.singleInbox.id)
        ) {
          this.targetInbox = this.singleInbox;
        } else {
          this.targetInbox =
            this.lastUsedInbox() || this.defaultInboxForContact();
        }
        this.isAssigningDefaultInbox = false;
      }
    },
    lastUsedInbox() {
      const lastUsedInboxId = this.uiSettings?.last_used_new_message_inbox_id;
      if (!lastUsedInboxId) return undefined;
      return this.inboxes.find(inbox => inbox.id === lastUsedInboxId);
    },
    defaultInboxForContact() {
      let inbox;
      if (this.contact.phone_number) {
        inbox = this.inboxes.find(x => x.api_type?.charAt(0) === 'w');
        inbox ||= this.inboxes.find(x => x.channel_type === 'Channel::Sms');
      }
      if (this.contact.email) {
        inbox ||= this.inboxes.find(x => x.channel_type === 'Channel::Email');
      }
      return inbox || this.inboxes[0];
    },
    setFocusToFirstInput() {
      this.$nextTick(() => {
        const submitButton = this.$el.querySelector('.button.primary');
        if (submitButton) {
          submitButton.setAttribute('tabindex', '-1');
          submitButton.focus();
        }
      });
    },
    setAttachmentPayload(payload) {
      this.attachedFiles.forEach(attachment => {
        if (this.globalConfig.directUploadsEnabled) {
          payload.files.push(attachment.blobSignedId);
        } else {
          payload.files.push(attachment.resource.file);
        }
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.contact.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    onCancel() {
      this.$emit('cancel');
    },
    replaceTextWithCannedResponse(message) {
      setTimeout(() => {
        this.message = message;
      }, 50);
    },
    prepareWhatsAppMessagePayload({
      message: content,
      templateParams,
      ...rest /* d99d */
    }) {
      const payload = {
        inboxId: this.targetInbox.id,
        contact: this.normalisedContact(),
        message: {
          content,
          template_params: templateParams,
          ...rest /* d99d */,
        },
        assigneeId: this.isAssigneeMe ? this.currentUser.id : undefined,
      };
      return payload;
    },
    onFormSubmit() {
      this.createConversation(this.emailMessagePayload);
    },
    async createConversation(payload) {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      try {
        const data = await this.onSubmit({
          params: payload,
          isFromWhatsApp: !this.isAnEmailChannel,
        });
        const action = this.isSingleWindow
          ? undefined
          : {
              type: 'link',
              to: `/app/accounts/${data.account_id}/conversations/${data.id}`,
              message: this.$t('NEW_CONVERSATION.FORM.GO_TO_CONVERSATION'),
            };
        this.showAlert(
          this.$t('NEW_CONVERSATION.FORM.SUCCESS_MESSAGE'),
          action
        );
      } catch (error) {
        // console.error(error);
        if (error instanceof ExceptionWithMessage) {
          this.showAlert(error.data);
        } else {
          this.showAlert(this.$t('NEW_CONVERSATION.FORM.ERROR_MESSAGE'));
        }
      }
    },

    toggleWaTemplate(val) {
      this.whatsappTemplateSelected = val;
    },
    async onSendWhatsAppReply(messagePayload) {
      const payload = this.prepareWhatsAppMessagePayload(messagePayload);
      await this.createConversation(payload);
    },
    normalisedContact() {
      const { phone_number, name, email } = this.contactInner;
      return { phone_number: this.israelPhone2e164(phone_number), name, email };
    },

    toggleEncryptFiles() {
      this.encryptFiles = !this.encryptFiles;
    },
    toggleDocal() {
      this.showDocalModal = !this.showDocalModal;
    },
    onDocalSigningLink(url) {
      this.showDocalModal = false;
      this.appendToMessage(url);
    },

    toggleForms() {
      this.showFormsModal = !this.showFormsModal;
    },
    onFormLink(url) {
      this.showFormsModal = false;
      this.appendToMessage(url);
    },
    appendToMessage(url) {
      const currentMsg = this.message || '';
      const separator = currentMsg.trim() ? ' ' : '';
      this.message = currentMsg + separator + url;
    },

    toggleCannedMenu(value) {
      // this.showCannedMenu = value; not implemented here
    },
    toggleMessageSignature() {
      this.updateUISettings({
        send_with_signature: !this.sendWithSignature,
      });
      this.setSignature();
    },
    setSignature() {
      if (this.messageSignature) {
        if (this.isSignatureEnabledForInbox) {
          this.message = appendSignature(this.message, this.signatureToApply);
        } else {
          this.message = removeSignature(this.message, this.signatureToApply);
        }
      }
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    onSelectContact(contact) {
      this.contactInner.phone_number = contact.phone_number;
      this.contactInner.name = contact.name;
    },
    onSelectContactEmail(contact) {
      this.contactInner.email = contact.email;
      this.contactInner.name = contact.name;
    },
    setPhoneNumber(value) {
      this.contactInner.phone_number = value;
    },
    setEmail(value) {
      this.contactInner.email = value;
    },
    emojiOnClick(emoji) {
      this.updateEditorSelectionWith = emoji;
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.conversation--form {
  padding: var(--space-normal) 24px 24px;
}

.canned-response {
  position: relative;
}

.input-group-label {
  font-size: var(--font-size-small);
}

.contact-input {
  display: flex;
  align-items: center;
  height: 3.9rem;
  background: var(--color-background-light);

  border: 1px solid var(--color-border);
  padding: var(--space-smaller) var(--space-small);
  border-radius: var(--border-radius-small);

  .contact-name {
    margin: 0;
    margin-left: var(--space-small);
  }
}

.message-input {
  min-height: 8rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 0px;
}

.encrypt-files-on {
  @apply bg-green-50;
  color: #22a06b !important;
}

.encrypt-files-chip {
  @apply flex items-center gap-1 h-8 px-2 rounded-md bg-green-50 text-green-700 text-xs whitespace-nowrap select-none;
}
.row.gutter-small {
  gap: var(--space-small);
}

::v-deep .mention--box {
  left: 0;
  margin: auto;
  right: 0;
  top: unset;
  height: fit-content;
}

::v-deep .ProseMirror.ProseMirror-woot-style {
  min-height: 13.5rem;
}

.file-uploads {
  @apply text-start;
}
.multiselect-wrap--small.has-multi-select-error {
  ::v-deep {
    .multiselect__tags {
      @apply border-red-500;
    }
  }
}

.emoji-dialog {
  @apply top-[unset] bottom-40 left-24 right-[unset];
  direction: ltr;

  // d99d
  .dashboard--blank & {
    left: 0;
  }
  // .

  &::before {
    transform: rotate(90deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));
    @apply right-80 bottom-2 rtl:right-0 rtl:left-80;
  }
}

.emoji-dialog--rtl {
  @apply left-[unset] right-24;

  // d99d
  .dashboard--blank & {
    right: 0;
  }
  // .
  &::before {
    transform: rotate(270deg);
    filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.08));

    // d99d
    .dashboard--blank & {
      left: unset !important;
      right: var(--space-normal);
    }
    // .
  }
}
</style>
